import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 4B-EXP (W3): reads the CANONICAL feature store
// (GET /api/company/{id}/features ← companies.enabled_features), mirroring the
// backend gate. The retired loadout/trial logic is gone — OFF means the owner
// turned the feature off (never "unpurchased").
const FeatureGuard = ({ addonId, children }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | allowed | blocked
  const [featureName, setFeatureName] = useState('');

  useEffect(() => {
    const checkAccess = async () => {
      const companyId = localStorage.getItem('company_id');
      if (!companyId) {
        setStatus('blocked');
        return;
      }
      try {
        const res = await axios.get(`${API}/api/company/${companyId}/features`);
        const enabled = res.data?.enabled_features || [];
        const allowed = enabled.includes(addonId);
        setStatus(allowed ? 'allowed' : 'blocked');
        setFeatureName(addonId.replace(/_/g, ' '));
        if (!allowed) {
          // Notify Ari that the user tried to reach a disabled feature
          try {
            const token = localStorage.getItem('access_token') || localStorage.getItem('token');
            await axios.post(
              `${API}/api/ari/events/emit`,
              {
                event_type: 'blocked_feature_access',
                data: { feature_key: addonId, feature_name: addonId },
              },
              token ? { headers: { Authorization: `Bearer ${token}` } } : undefined,
            );
          } catch { /* non-blocking */ }
        }
      } catch {
        setStatus('blocked');
      }
    };
    checkAccess();
  }, [addonId]);

  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-200 via-slate-100 to-blue-100 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  if (status === 'blocked') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-200 via-slate-100 to-blue-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl border-2 border-gray-200 p-8 max-w-md text-center shadow-sm">
          <ShieldOff className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2 capitalize">{featureName || 'Feature'} is turned off</h2>
          <p className="text-sm text-gray-600 mb-6">
            This feature is included with your RemodDash membership — it's just switched off for
            your workspace right now. Turn it back on in Settings → Features, or ask Ari.
          </p>
          <div className="flex gap-3 justify-center">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="rounded-xl"
              data-testid="back-to-dashboard-btn"
            >
              Back to Dashboard
            </Button>
            <Button
              onClick={() => navigate('/settings/features')}
              className="rounded-xl bg-blue-600 hover:bg-blue-700"
              data-testid="enable-feature-btn"
            >
              Turn On in Settings
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default FeatureGuard;
